import type { FC } from "react";
import { memo, useCallback } from "react";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import {
  EventGridListItem,
  EventGridListItemBody,
  EventGridListItemCalendar,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemTags,
  EventGridListItemVariant,
} from "@stubhub/ensemble-discovery";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { type EventListGridItem, EventState, GridType } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useCommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/use-common-event-grid-list-item";
import { TOP_LEVEL_CATEGORY_IDS } from "src/website-apps/shared/components/explore-events/constants";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { useIsIsWorldCupCategory } from "src/website-apps/shared/hooks/useIsWorldCupCategory";
import { useLeague } from "src/website-apps/shared/hooks/useLeague";
import { getPerformerLeagueIdFromModel } from "src/website-apps/shared/utils/getPerformerLeagueIdFromModel";
import { usePerformerPageModelData } from "../../hooks/usePerformerPageModelData";
import { PerformerEventGridListItemBodyContent } from "../performer-event-grid-list-item/performer-event-grid-list-item-body-content";
import { useRecentlyViewedMerchTag } from "./useRecentlyViewedMerchTag";

interface RecentlyViewedEventItemProps {
  event: EventListGridItem;
  position: number;
  onItemClick: (itemId: string | number) => void;
  compactSubtitle?: boolean;
}

/**
 * Simplified event card for Recently Viewed Events carousel.
 *
 * Uses the shared PerformerEventGridListItemBodyContent for title/subtitle rendering
 * (including Team vs Team for sports) but with merchandising tags hidden.
 */
export const RecentlyViewedEventItem: FC<RecentlyViewedEventItemProps> = memo(
  ({ event, position, onItemClick, compactSubtitle = false }) => {
    const model = usePerformerPageModelData();
    const isSport = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.SPORT;
    const isTheater = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.THEATER;
    const isConcert = model.categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY_IDS.CONCERT;
    const leagueId = getPerformerLeagueIdFromModel(model);
    const { isMLB, isNBA, isNFL, isNHL } = useLeague(leagueId);
    const isWorldCup = useIsIsWorldCupCategory();

    const handleItemClick = useCallback(
      (id: number) => {
        onItemClick(String(id));
      },
      [onItemClick],
    );

    const {
      isStubhub,
      eventUrl,
      eventState,
      venueLocation,
      onSaleTimeMillis,
      flagCountries,
      defaultTags,
      merchInViewTrackers,
      handleOnClick,
      handleOnInView,
    } = useCommonEventGridListItem({
      event,
      gridType: GridType.RecentlyViewed,
      index: position,
      onClick: handleItemClick,
      previewSummary: { name: event.name, imageUrl: model.categorySummary.portalImageUrl ?? null },
    });

    const recentlyViewedMerchTags = useRecentlyViewedMerchTag(event);

    const shouldFlipTitles = !isStubhub && (isTheater || isConcert);
    const canParticipateFlippedTitles = useCallback(() => shouldFlipTitles, [shouldFlipTitles]);
    const locationPriority =
      useSimpleFeatureTernary(
        FeatureSetEnum.TMPerformerViagogoStandardConcertEventTitles,
        shouldFlipTitles,
        false,
        canParticipateFlippedTitles,
      ) ?? shouldFlipTitles;

    return (
      <>
        <InViewTracker onInView={handleOnInView} />
        <EventGridListItem
          href={eventUrl ?? "#"}
          onClick={handleOnClick}
          variant={EventGridListItemVariant.Outline}
          kind={isSport ? EventGridListItemKind.Neutral : EventGridListItemKind.Brand}
          eventState={eventState}
          eventType={event.isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
          eventTitle={event.name}
          venueName={event.venueName ?? ""}
          venueLocation={venueLocation}
          venueCountryCode={event.countryCode as FlagCountry}
          startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
          endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
          onSaleTimeMillis={onSaleTimeMillis}
          venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset ?? undefined}
          passLength={event.eventMetadata?.common?.passLength ?? undefined}
          isDateConfirmed={event.isDateConfirmed}
          isTimeConfirmed={event.isTimeConfirmed}
          isPostponed={event.eventState === EventState.Postponed}
        >
          <EventGridListItemCalendar />
          <EventGridListItemBody flagCountries={flagCountries}>
            {merchInViewTrackers}
            <PerformerEventGridListItemBodyContent
              event={event}
              isNBA={isNBA}
              isNFL={isNFL}
              isNHL={isNHL}
              isMLB={isMLB}
              isNationalTeam={isWorldCup}
              locationPriority={locationPriority}
              hideMerchTags
              compactSubtitle={compactSubtitle}
              tags={defaultTags}
            />
            {recentlyViewedMerchTags.length > 0 && <EventGridListItemTags legacy tags={recentlyViewedMerchTags} />}
          </EventGridListItemBody>
        </EventGridListItem>
      </>
    );
  },
);

RecentlyViewedEventItem.displayName = "RecentlyViewedEventItem";
